import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Send, CheckCircle, Clock, MessageSquare } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      content: 'contact@onlinemmi.online',
      link: 'mailto:contact@onlinemmi.online',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Phone,
      title: 'Call Our Voice AI (Live Demo)',
      content: '07427 860249',
      subtext: 'Call this number to hear our AI agent in action — the same technology we build for your business.',
      link: 'tel:07427860249',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Mon-Fri: 9AM - 6PM GMT',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  const benefits = [
    'Free consultation call',
    'Custom AI strategy',
    'ROI analysis',
    'No obligation demo',
    '24/7 support available',
    'Flexible pricing options'
  ];

  return (
    <div className="pt-16">
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary-600 via-accent-600 to-primary-700 text-white">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
            <span className="text-white font-semibold text-sm">Get In Touch</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 leading-tight">
            Let's Start Your
            <br />
            <span className="text-primary-200">AI Journey</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto leading-relaxed font-medium">
            Schedule a call to discover how AI can transform your business and unlock new revenue streams
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover-lift border border-gray-100 text-center"
              >
                <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${info.gradient} mb-6 group-hover:scale-110 transition-transform`}>
                  <info.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-display font-bold mb-3 text-gray-900">
                  {info.title}
                </h3>
                {info.link ? (
                  <a
                    href={info.link}
                    className="text-gray-600 hover:text-primary-600 transition-colors font-medium"
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="text-gray-600 font-medium">{info.content}</p>
                )}
                {info.subtext && (
                  <p className="text-gray-500 text-sm mt-2 leading-relaxed">{info.subtext}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zoho Calendar Booking Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              <span className="text-gradient">Book Your Free Consultation</span>
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto font-medium">
              Schedule a call directly with our team to discuss how AI can transform your business
            </p>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-primary-50 rounded-3xl p-8 shadow-xl flex justify-center">
            <iframe
              src="https://calendar.zoho.eu/eventreqForm/zz0801123037ab74bfe19ee7c6f8906792502f79a0c935a91417e5082bd44379ccddd02d98ebfd6f1205bfec989d495a72098e2c7a?theme=0&l=en&tz=Europe%2FLondon"
              title="contact"
              frameBorder="0"
              scrolling="no"
              marginWidth="0"
              marginHeight="0"
              height="350px"
              width="432px"
              allowTransparency="true"
              align="center"
            ></iframe>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
                <span className="text-gradient">Or Send Us a Message</span>
              </h2>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed font-medium">
                Fill out the form and we'll get back to you within 24 hours. Let's discuss how AI can help you achieve your business goals.
              </p>

              <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
                <h3 className="text-2xl font-display font-bold mb-6 text-gray-900">
                  What You'll Get:
                </h3>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="w-6 h-6 text-primary-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-primary-600 to-accent-600 rounded-2xl p-8 text-white">
                <MessageSquare className="w-12 h-12 mb-4" />
                <h3 className="text-2xl font-display font-bold mb-3">
                  Prefer to Talk?
                </h3>
                <p className="text-white mb-4 font-medium">
                  Book a call directly with our team to discuss your specific needs and see a live demo of our AI solutions.
                </p>
                <a
                  href="tel:07427860249"
                  className="inline-block bg-white text-primary-600 px-6 py-3 rounded-full font-bold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Call Now
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="inline-flex p-4 rounded-full bg-green-100 mb-6">
                    <CheckCircle className="w-16 h-16 text-green-600" />
                  </div>
                  <h3 className="text-3xl font-display font-bold mb-4 text-gray-900">
                    Thank You!
                  </h3>
                  <p className="text-xl text-gray-600">
                    We've received your message and will get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none"
                      placeholder="+44 123 456 7890"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none"
                      placeholder="Your Company Ltd"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none resize-none"
                      placeholder="Tell us about your business and how we can help..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-primary-600 to-accent-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center"
                  >
                    <span>Send Message</span>
                    <Send className="w-5 h-5 ml-2" />
                  </button>

                  <p className="text-sm text-gray-500 text-center">
                    By submitting this form, you agree to our <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-primary-600 hover:underline">Terms of Service</Link>.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-12 md:p-16 text-center text-white shadow-2xl">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
              Pull 5 or 6 Figures Worth of Sales
            </h2>
            <p className="text-xl text-white mb-8 max-w-3xl mx-auto leading-relaxed font-medium">
              Our Database Reactivation bot helps businesses treat every lead like it's their last. The bot takes all the people in a database who haven't bought and strikes up a conversation with them via SMS.
            </p>
            <p className="text-xl text-white mb-10 max-w-2xl mx-auto font-medium">
              Then next thing you know, they're booking calls and you're up <span className="font-bold text-white">£10K-£100K+ in sales</span>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact-form"
                className="inline-flex items-center justify-center bg-gradient-to-r from-primary-600 to-accent-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
              >
                Schedule Your Demo
                <Send className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
